// src/screens/CreatorMembershipsScreen.js
// ============================================================================
// CHANNEL MEMBERSHIPS (evergreen 3.5a, C4): the creator's own settings.
//
// Reached from Streaming Hub > Your Stream (getMyStreamInfo.membershipEntry)
// and from the new-member / status notices. Exempt from the Terms prompt
// (App.js HIDE_TOS_PROMPT_ROUTES), so pausing, ending, removing a member and
// turning members-only chat off never wait on accepting new Terms; the Terms
// row below is a nudge (the server decides, 5.9).
//
// Data: getMyMembershipSettings (functions/modules/memberships.js, 5.8) is the
// creator's ONLY view of their members: stored price and perks, eligibility,
// the member list (names, tenure, ending, removed), removed viewers, the chat
// mode. Never prices paid, payment problems or end reasons. Every change goes
// through one callable and the screen redraws from its reply; after ANY
// failed change the settings are fetched again (the checklist redraws).
//
// When `available` is false (memberships dark and no pilot) the screen is a
// management-only view: members, Remove member / Allow again, End all, perk
// removals and members-only chat OFF stay; everything that opens the channel
// to new members is hidden. So a creator with renewing members is never
// locked out.
//
// Tiers (R5, 2.7.3, 5.12): four tier cards, each with "Offer {Tier}", a price
// stepper that walks that tier's points with the exact estimate for the
// point shown, the band line, and "Save {Tier} price" behind a confirm (so
// stepping never uses up the 30-day change), then a lock line until the
// date. Each perk has a "Starts at" picker (Off or a tier; each tier includes
// the perks below it) and a "What each tier gets" summary. The member list
// shows each member's tier, the heading the count per tier.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, ActivityIndicator, StyleSheet, SafeAreaView, Modal, Pressable, Switch,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebaseConfig';
import { THEME } from '../constants/uiTheme';
import { useError } from '../components/ErrorContext';
import Header from '../components/Header';
import useMembershipsLive from '../hooks/useMembershipsLive';
import {
  getMyMembershipSettings,
  updateMyMembershipSettings,
  endAllMyMemberships,
  setChannelChatMembersOnly,
  setChannelMemberBlocked,
  membershipErrorMessage,
  logMembershipError,
  formatUsd,
  formatMonthlyPrice,
  LISTED_PERKS,
  PERK_LABELS,
} from '../utils/membershipPayment';
import {
  tierNameOf,
  tierRankOf,
  tierPerks,
  tierPerkLines,
  stepPoint,
  pointsOf,
  estShareFor,
  longDate,
  lowestOnRank,
  tierCountsLine,
  TIERS_TITLE,
  TIERS_LEAD,
  offerTierLabel,
  bandLine,
  lowerPriceA11y,
  raisePriceA11y,
  savePriceLabel,
  setPriceTitle,
  SET_PRICE_BODY_FIRST,
  SET_PRICE_BODY_LOCKING,
  SET_PRICE_OK,
  SET_PRICE_CANCEL,
  priceLockLine,
  tierOffHint,
  PERK_RULES,
  STARTS_AT_LABEL,
  STARTS_AT_OPTIONS,
  WHAT_EACH_TIER_GETS,
  chatConfirmTier,
  perkMoveTitle,
} from '../utils/membershipTiers';

const C = THEME.colors;
const ERROR_TITLE = 'Channel memberships';
// The three member perks (each starts at a tier); the join line is a switch
// (an option for new joins only).
const SNAPSHOT_PERKS = LISTED_PERKS;
// "Turn off {perk}?" nouns (the Terms section 6 nouns, as BN's notices).
const PERK_OFF_NAMES = {
  badge: 'the member badge in chat',
  membersChat: 'members-only chat',
  membersVideos: 'members-only videos',
};
const VIDEOS_STAY_LINE = 'Your members-only videos stay visible only to current members until their access ends, then only to you.';

const fmtMonthYear = (ms) => new Date(ms).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
const perkLabel = (k) => PERK_LABELS[k];

export function introText(sharePct) {
  const pct = Math.round(Number(sharePct));
  return 'Let your viewers support you every month. CritCash credits you a share of each membership payment, '
    + `currently up to ${pct}% after processing costs. Each share becomes payable no sooner than 48 hours after `
    + 'the payment, and not before CritCash has received the funds.';
}

export function rungEstimateText(estShareCents) {
  return `CritCash currently credits you about ${formatUsd(estShareCents)} for each membership payment at this price `
    + '(an estimate, after processing costs)';
}

export function membersHeading(members) {
  const n = Array.isArray(members?.list) ? members.list.length : 0;
  if (members?.truncated === true) return '500+ members';
  return `${n} ${n === 1 ? 'member' : 'members'}`;
}

const monthsText = (m) => {
  const n = Math.max(0, Number(m) || 0);
  return `${n} ${n === 1 ? 'month' : 'months'}`;
};

export default function CreatorMembershipsScreen({ navigation }) {
  const { showError } = useError();
  const { live: membershipsLive } = useMembershipsLive();
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [perkNotice, setPerkNotice] = useState(null);
  // The price each tier's stepper shows before it is saved: {[tierKey]: cents}.
  const [draftPrice, setDraftPrice] = useState({});
  // One confirm at a time: {kind:'perkOff', perk} | {kind:'perkUp', perk,
  // tierKey} | {kind:'price', tierKey, cents, everOn} | {kind:'chatOn'} |
  // {kind:'remove', row} | {kind:'allow', row} | {kind:'endAll'}.
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return unsub;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setData(await getMyMembershipSettings({}));
    } catch (err) {
      console.error('[CreatorMembershipsScreen] load failed:', err);
      setLoadError(membershipErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authReady && user) load();
    if (authReady && !user) setData(null);
  }, [authReady, user, load]);

  // After ANY failed change: the server's sentence, the confirm closed, and
  // the settings fetched again (eligibility.reason redraws the checklist).
  const failed = (err) => {
    logMembershipError('CreatorMembershipsScreen change', err);
    setConfirm(null);
    showError(ERROR_TITLE, membershipErrorMessage(err));
    load();
  };

  const run = async (key, fn) => {
    setBusy(key);
    try {
      return await fn();
    } catch (err) {
      failed(err);
      return undefined;
    } finally {
      setBusy(null);
    }
  };

  // updateMyMembershipSettings answers with the full settings shape; keep the
  // removed-viewers pages already loaded (the reply carries the first page).
  const applySettings = (next) => {
    if (next && typeof next === 'object') setData(next);
  };

  const setEnabled = (enabled) => run('enabled', async () => {
    setPerkNotice(null);
    applySettings(await updateMyMembershipSettings({ enabled }));
  });

  // A tier on or off (2.7.3). The server refuses turning off the last tier
  // while new members can join (C11).
  const setTierOn = (tierKey, on) => run(`tier:${tierKey}`, async () => {
    setPerkNotice(null);
    applySettings(await updateMyMembershipSettings({ tiers: { [tierKey]: { on } } }));
  });

  // A tier's price, after the confirm (C3). The draft clears either way: the
  // stepper then shows the saved price.
  const setTierPrice = (tierKey, priceCents) => run(`price:${tierKey}`, async () => {
    setPerkNotice(null);
    try {
      const next = await updateMyMembershipSettings({ tiers: { [tierKey]: { priceCents } } });
      setConfirm(null);
      applySettings(next);
    } finally {
      setDraftPrice((prev) => {
        const out = { ...prev };
        delete out[tierKey];
        return out;
      });
    }
  });

  const stepPrice = (t, dir) => {
    const shown = draftPrice[t.tierKey] ?? t.priceCents;
    const next = stepPoint(t, shown, dir);
    if (next == null) return;
    setDraftPrice((prev) => {
      const out = { ...prev };
      if (next === t.priceCents) delete out[t.tierKey];
      else out[t.tierKey] = next;
      return out;
    });
  };

  // A perk's starting tier (null = off). Narrowing (off, or a higher start)
  // tells members, so it asks first; widening is immediate.
  const writePerkTier = (perk, tierKey) => run(`perk:${perk}`, async () => {
    const before = data?.settings?.perkTiers?.[perk] ?? null;
    const next = await updateMyMembershipSettings({ perkTiers: { [perk]: tierKey } });
    setConfirm(null);
    applySettings(next);
    const narrowed = before != null && (tierKey == null || tierRankOf(tierKey) > tierRankOf(before));
    setPerkNotice(narrowed ? 'Saved. Members will be notified.' : null);
  });

  const onPerkTierChange = (perk, tierKey) => {
    setPerkNotice(null);
    const before = data?.settings?.perkTiers?.[perk] ?? null;
    if (tierKey === before) return;
    if (before != null && tierKey == null) setConfirm({ kind: 'perkOff', perk });
    else if (before != null && tierRankOf(tierKey) > tierRankOf(before)) setConfirm({ kind: 'perkUp', perk, tierKey });
    else writePerkTier(perk, tierKey);
  };

  // The join shout-out (an option for new joins only).
  const setJoinLine = (on) => run('perk:joinLine', async () => {
    setPerkNotice(null);
    applySettings(await updateMyMembershipSettings({ perks: { joinLine: on } }));
  });

  const setChat = (on) => run('chat', async () => {
    await setChannelChatMembersOnly({ on });
    setConfirm(null);
    await load();
  });

  const setBlocked = (viewerUid, blocked) => run(`block:${viewerUid}`, async () => {
    await setChannelMemberBlocked({ viewerUid, blocked });
    setConfirm(null);
    await load();
  });

  const endAll = () => run('endAll', async () => {
    await endAllMyMemberships({ confirm: true });
    setConfirm(null);
    await load();
  });

  const showMoreBlocked = () => run('blockedMore', async () => {
    const list = data?.blocked || [];
    const last = list[list.length - 1];
    if (!last) return;
    const page = await getMyMembershipSettings({ blockedAfter: last.viewerUid });
    setData((prev) => (prev ? {
      ...prev,
      blocked: [...(prev.blocked || []), ...(Array.isArray(page?.blocked) ? page.blocked : [])],
      blockedTruncated: page?.blockedTruncated === true,
    } : prev));
  });

  // --------------------------------------------------------------------------
  // Sections
  // --------------------------------------------------------------------------

  const renderChecklist = () => {
    const el = data.eligibility || {};
    const rows = [];
    if (el.ok === false && el.message) {
      rows.push(
        <View key="elig" style={styles.checkRow}>
          <Ionicons name="alert-circle-outline" size={18} color={C.brand} style={styles.checkIcon} />
          <View style={styles.checkBody}>
            <Text style={styles.checkText}>{el.message}</Text>
            {el.reason === 'unverified' ? (
              <TouchableOpacity style={styles.linkBtn} onPress={() => navigation.navigate('Verification')} accessibilityRole="button">
                <Text style={styles.linkBtnText}>Verify your ID and age</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        </View>
      );
    }
    if (data.termsCurrent === false) {
      rows.push(
        <View key="terms" style={styles.checkRow}>
          <Ionicons name="document-text-outline" size={18} color={C.brand} style={styles.checkIcon} />
          <View style={styles.checkBody}>
            <TouchableOpacity style={styles.linkBtn} onPress={() => navigation.navigate('TosAgreement')} accessibilityRole="button">
              <Text style={styles.linkBtnText}>Review and accept the updated Terms</Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }
    return rows.length ? <View style={styles.section}>{rows}</View> : null;
  };

  const renderAccept = () => {
    const s = data.settings || {};
    const on = s.enabled === true;
    return (
      <View style={styles.section}>
        <View style={styles.switchRow}>
          <Text style={styles.switchLabel}>Accept new members</Text>
          <Switch
            value={on}
            onValueChange={(v) => setEnabled(v)}
            disabled={!!busy}
            trackColor={{ false: '#333', true: C.brand }}
            thumbColor={on ? '#000' : '#888'}
            accessibilityLabel="Accept new members"
          />
        </View>
        {!on && (s.paused === true || s.ended === true) ? (
          <Text style={styles.hint}>
            {"New viewers can't join while you're not accepting new members. Current members keep their perks and renewals. "}
            {VIDEOS_STAY_LINE}
          </Text>
        ) : null}
      </View>
    );
  };

  // The settings tiers (2.7.3), valid rows only, by rank.
  const settingsTiers = () => (Array.isArray(data?.settings?.tiers) ? data.settings.tiers : [])
    .filter((t) => t && tierNameOf(t.tierKey) && Number.isInteger(t.priceCents))
    .sort((a, b) => tierRankOf(a.tierKey) - tierRankOf(b.tierKey));

  const renderTierCard = (t) => {
    const name = tierNameOf(t.tierKey);
    const on = t.on === true;
    const shown = draftPrice[t.tierKey] ?? t.priceCents;
    const pts = pointsOf(t);
    const locked = Number(t.priceLockedUntilMs) > Date.now();
    const atMin = !pts.length || shown <= pts[0];
    const atMax = !pts.length || shown >= pts[pts.length - 1];
    const est = estShareFor(t, shown);
    const changed = shown !== t.priceCents;
    const stepBtn = (dir, label, icon, edge) => (
      <TouchableOpacity
        style={[styles.stepBtn, (edge || locked || !!busy) && styles.stepBtnOff]}
        onPress={() => stepPrice(t, dir)}
        disabled={edge || locked || !!busy}
        accessibilityRole="button"
        accessibilityLabel={label}
      >
        <Ionicons name={icon} size={18} color={C.text} />
      </TouchableOpacity>
    );
    return (
      <View key={t.tierKey} style={[styles.tierCard, on && styles.tierCardOn]}>
        <View style={styles.switchRow}>
          <Text style={styles.tierCardName}>{offerTierLabel(name)}</Text>
          <Switch
            value={on}
            onValueChange={(v) => setTierOn(t.tierKey, v)}
            disabled={!!busy}
            trackColor={{ false: '#333', true: C.brand }}
            thumbColor={on ? '#000' : '#888'}
            accessibilityLabel={offerTierLabel(name)}
          />
        </View>
        <View style={styles.stepRow}>
          {stepBtn(-1, lowerPriceA11y(name), 'remove', atMin)}
          <Text style={styles.stepPrice}>{formatMonthlyPrice(shown)}</Text>
          {stepBtn(1, raisePriceA11y(name), 'add', atMax)}
        </View>
        {est != null ? <Text style={styles.rungEst}>{rungEstimateText(est)}</Text> : null}
        <Text style={styles.bandText}>{bandLine(name, t.minCents, t.maxCents)}</Text>
        {changed && !locked ? (
          <TouchableOpacity
            style={[styles.saveBtn, !!busy && styles.stepBtnOff]}
            onPress={() => setConfirm({ kind: 'price', tierKey: t.tierKey, cents: shown, everOn: t.everOn === true })}
            disabled={!!busy}
            accessibilityRole="button"
          >
            <Text style={styles.saveBtnText}>{savePriceLabel(name)}</Text>
          </TouchableOpacity>
        ) : null}
        {locked ? <Text style={styles.hint}>{priceLockLine(name, longDate(t.priceLockedUntilMs))}</Text> : null}
        {on && t.everOn === true ? <Text style={styles.hint}>{tierOffHint(name)}</Text> : null}
      </View>
    );
  };

  const renderTiers = () => {
    const tiers = settingsTiers();
    if (!tiers.length) return null;
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{TIERS_TITLE}</Text>
        <Text style={styles.sectionLead}>{TIERS_LEAD}</Text>
        {tiers.map(renderTierCard)}
      </View>
    );
  };

  const renderPerks = (available) => {
    const s = data.settings || {};
    const perkTiers = s.perkTiers || {};
    const joinLineOn = s.perks?.joinLine === true;
    const tiers = settingsTiers();
    // "What each tier gets": the tiers that are on (all four while none is),
    // each with the lines a fan sees when joining.
    const shownTiers = tiers.some((t) => t.on === true) ? tiers.filter((t) => t.on === true) : tiers;
    const summary = tierPerkLines(shownTiers.map((t) => ({ tierKey: t.tierKey, perks: tierPerks(perkTiers, t.tierKey) })));
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Member perks</Text>
        {SNAPSHOT_PERKS.map((k) => {
          const cur = perkTiers[k] ?? null;
          const curRank = tierRankOf(cur);
          return (
            <View key={k} style={styles.perkBlock}>
              <Text style={styles.switchLabel}>{perkLabel(k)}</Text>
              <Text style={styles.startsAtLabel}>{STARTS_AT_LABEL}</Text>
              <View style={styles.pickRow} accessibilityRole="radiogroup" accessibilityLabel={`${perkLabel(k)}: ${STARTS_AT_LABEL}`}>
                {STARTS_AT_OPTIONS.map((o) => {
                  const selected = (o.value ?? null) === cur;
                  // Management-only: a perk can be narrowed or turned off,
                  // never widened (an earlier start, or on from off).
                  const widens = o.value != null && (cur == null || tierRankOf(o.value) < curRank);
                  const off = !!busy || (!available && widens);
                  return (
                    <TouchableOpacity
                      key={o.label}
                      style={[styles.pickChip, selected && styles.pickChipOn, off && !selected && styles.stepBtnOff]}
                      onPress={() => { if (!selected && !off) onPerkTierChange(k, o.value); }}
                      disabled={off}
                      accessibilityRole="radio"
                      accessibilityState={{ selected, disabled: off }}
                      accessibilityLabel={o.label}
                    >
                      <Text style={[styles.pickChipText, selected && styles.pickChipTextOn]}>{o.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          );
        })}
        <View style={styles.switchRow}>
          <Text style={styles.switchLabel}>{perkLabel('joinLine')}</Text>
          <Switch
            value={joinLineOn}
            onValueChange={(v) => setJoinLine(v)}
            disabled={!!busy || (!available && !joinLineOn)}
            trackColor={{ false: '#333', true: C.brand }}
            thumbColor={joinLineOn ? '#000' : '#888'}
            accessibilityLabel={perkLabel('joinLine')}
          />
        </View>
        <Text style={styles.hint}>{PERK_RULES}</Text>
        {perkNotice ? <Text style={styles.notice}>{perkNotice}</Text> : null}
        {summary.length ? (
          <View style={styles.summary}>
            <Text style={styles.summaryTitle}>{WHAT_EACH_TIER_GETS}</Text>
            {summary.map((row) => (
              <View key={row.tierKey} style={styles.summaryRow}>
                <Text style={styles.summaryTier}>{tierNameOf(row.tierKey)}</Text>
                {row.lines.map((l) => <Text key={l} style={styles.summaryLine}>{`• ${l}`}</Text>)}
              </View>
            ))}
          </View>
        ) : null}
      </View>
    );
  };

  const renderChat = (available) => {
    const chat = data.chat || {};
    const membersOnly = chat.membersOnly === true;
    if (!membersOnly && !(available && chat.available === true)) return null;
    if (membersOnly && chat.paused === true) {
      return (
        <View style={styles.section}>
          <View style={styles.switchRow}>
            <Text style={styles.switchLabel}>Members-only chat (paused)</Text>
            <TouchableOpacity style={styles.smallBtn} onPress={() => setChat(false)} disabled={!!busy} accessibilityRole="button">
              <Text style={styles.smallBtnText}>Turn off</Text>
            </TouchableOpacity>
          </View>
          <Text style={styles.hint}>Members-only chat is paused while memberships are off. Anyone can chat right now.</Text>
        </View>
      );
    }
    return (
      <View style={styles.section}>
        <View style={styles.switchRow}>
          <Text style={styles.switchLabel}>Members-only chat</Text>
          <Switch
            value={membersOnly}
            onValueChange={(v) => (v ? setConfirm({ kind: 'chatOn' }) : setChat(false))}
            disabled={!!busy || (!membersOnly && !(available && chat.available === true))}
            trackColor={{ false: '#333', true: C.brand }}
            thumbColor={membersOnly ? '#000' : '#888'}
            accessibilityLabel="Members-only chat"
          />
        </View>
      </View>
    );
  };

  const renderMembers = () => {
    const members = data.members || {};
    const list = Array.isArray(members.list) ? members.list : [];
    // Per-tier counts (the same current-member test as Creator Metrics).
    const counts = tierCountsLine(settingsTiers());
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{membersHeading(members)}</Text>
        {counts ? <Text style={styles.tierCounts}>{counts}</Text> : null}
        {list.map((m) => (
          <View key={m.viewerUid} style={styles.memberRow}>
            <View style={styles.memberBody}>
              <Text style={styles.memberName} numberOfLines={1}>{m.name || 'A member'}</Text>
              <Text style={styles.memberMeta}>
                {Number(m.sinceMs) > 0 ? `Member since ${fmtMonthYear(m.sinceMs)}` : ''}
                {Number(m.sinceMs) > 0 ? ' · ' : ''}
                {monthsText(m.months)}
              </Text>
              <View style={styles.chipRow}>
                {tierNameOf(m.tierKey) ? <Text style={[styles.chip, styles.chipTier]}>{tierNameOf(m.tierKey)}</Text> : null}
                {m.ending === true ? <Text style={styles.chip}>Ending</Text> : null}
                {m.removed === true ? <Text style={[styles.chip, styles.chipWarn]}>Removed</Text> : null}
              </View>
            </View>
            {m.removed !== true ? (
              <TouchableOpacity
                style={styles.smallBtn}
                onPress={() => setConfirm({ kind: 'remove', row: m })}
                disabled={!!busy}
                accessibilityRole="button"
              >
                <Text style={styles.smallBtnDanger}>Remove member</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ))}
      </View>
    );
  };

  const renderBlocked = () => {
    const blocked = Array.isArray(data.blocked) ? data.blocked : [];
    if (!blocked.length) return null;
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Removed viewers</Text>
        {blocked.map((b) => (
          <View key={b.viewerUid} style={styles.memberRow}>
            <View style={styles.memberBody}>
              <Text style={styles.memberName} numberOfLines={1}>{b.name || 'A viewer'}</Text>
            </View>
            <TouchableOpacity
              style={styles.smallBtn}
              onPress={() => setConfirm({ kind: 'allow', row: b })}
              disabled={!!busy}
              accessibilityRole="button"
            >
              <Text style={styles.smallBtnText}>Allow again</Text>
            </TouchableOpacity>
          </View>
        ))}
        {data.blockedTruncated === true ? (
          <TouchableOpacity style={styles.linkBtn} onPress={showMoreBlocked} disabled={!!busy} accessibilityRole="button">
            <Text style={styles.linkBtnText}>Show more</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    );
  };

  const renderEndAll = () => {
    const list = data.members?.list || [];
    if (!list.length && data.settings?.enabled !== true) return null;
    return (
      <View style={styles.section}>
        <TouchableOpacity style={styles.dangerBtn} onPress={() => setConfirm({ kind: 'endAll' })} disabled={!!busy} accessibilityRole="button">
          <Text style={styles.dangerBtnText}>End all memberships</Text>
        </TouchableOpacity>
      </View>
    );
  };

  // --------------------------------------------------------------------------
  // Confirm dialog
  // --------------------------------------------------------------------------

  const confirmSpec = () => {
    if (!confirm) return null;
    if (confirm.kind === 'perkOff') {
      const body = ["Each member keeps it until their first renewal at least 7 days from now, and we'll let your members know."];
      if (confirm.perk === 'membersVideos') body.push(`${VIDEOS_STAY_LINE} You can make them Public or Private in My videos.`);
      return {
        title: `Turn off ${PERK_OFF_NAMES[confirm.perk]}?`,
        body,
        cancel: 'Keep it on',
        ok: 'Turn off',
        danger: true,
        onOk: () => writePerkTier(confirm.perk, null),
      };
    }
    if (confirm.kind === 'perkUp') {
      const tName = tierNameOf(confirm.tierKey);
      return {
        title: perkMoveTitle(PERK_OFF_NAMES[confirm.perk], confirm.tierKey),
        body: [`Members below ${tName} keep it until their first renewal at least 7 days from now, and we'll let them know.`],
        cancel: 'Keep it as is',
        ok: 'Move',
        onOk: () => writePerkTier(confirm.perk, confirm.tierKey),
      };
    }
    if (confirm.kind === 'price') {
      const tName = tierNameOf(confirm.tierKey);
      return {
        title: setPriceTitle(tName, confirm.cents),
        body: [confirm.everOn ? SET_PRICE_BODY_LOCKING : SET_PRICE_BODY_FIRST],
        cancel: SET_PRICE_CANCEL,
        ok: SET_PRICE_OK,
        onOk: () => setTierPrice(confirm.tierKey, confirm.cents),
      };
    }
    if (confirm.kind === 'chatOn') {
      // C7: with a minimum tier above the lowest tier on, name it.
      const s = data?.settings || {};
      const minRank = tierRankOf(s.perkTiers?.membersChat);
      const lowRank = lowestOnRank(settingsTiers().filter((t) => t.on === true));
      const body = [minRank > 1 && minRank > Math.max(1, lowRank)
        ? chatConfirmTier(s.perkTiers.membersChat)
        : 'Only members can type. Super Crits, Rumbles and lines from your linked chats still show.'];
      if (data?.settings?.paused === true || !membershipsLive) {
        body.push("New viewers can't join right now, so only current members will be able to type.");
      }
      return { title: 'Members-only chat', body, cancel: 'Cancel', ok: 'Turn on', onOk: () => setChat(true) };
    }
    if (confirm.kind === 'remove') {
      return {
        title: confirm.row?.name ? `Remove ${confirm.row.name}?` : 'Remove this member?',
        body: ["For members who break your chat rules. Their membership stops renewing and ends when their paid month does. They lose members-only chat and their member badge now, and they can't join your channel again unless you allow it."],
        cancel: 'Keep member',
        ok: 'Remove',
        danger: true,
        onOk: () => setBlocked(confirm.row.viewerUid, true),
      };
    }
    if (confirm.kind === 'allow') {
      return {
        title: confirm.row?.name ? `Allow ${confirm.row.name} again?` : 'Allow this viewer again?',
        body: ['They can join your channel again after any current membership ends.'],
        cancel: 'Cancel',
        ok: 'Allow',
        onOk: () => setBlocked(confirm.row.viewerUid, false),
      };
    }
    if (confirm.kind === 'endAll') {
      return {
        title: 'End all memberships?',
        body: ["Every membership ends at the end of the month it's paid for, and no one is charged again. Members keep their perks until then. Your members-only videos then stay visible only to you until you change them."],
        cancel: 'Keep them',
        ok: 'End all',
        danger: true,
        onOk: endAll,
      };
    }
    return null;
  };
  const spec = confirmSpec();

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  const available = data?.available === true;
  const pct = Number(data?.sharePct);

  return (
    <SafeAreaView style={styles.safe}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.inner}>
          <Text style={styles.title}>Channel memberships</Text>

          {!authReady ? (
            <View style={styles.stateBlock}><ActivityIndicator color={C.brand} size="large" /></View>
          ) : !user ? (
            <View style={styles.stateBlock}>
              <Ionicons name="lock-closed" size={36} color="#666" />
              <Text style={styles.stateText}>Sign in to manage your channel memberships.</Text>
              <TouchableOpacity style={styles.primaryBtn} onPress={() => navigation.navigate('Login')} accessibilityRole="button">
                <Text style={styles.primaryBtnText}>Sign in</Text>
              </TouchableOpacity>
            </View>
          ) : !data && (loading || !loadError) ? (
            <View style={styles.stateBlock}><ActivityIndicator color={C.brand} size="large" /></View>
          ) : !data ? (
            <View style={styles.stateBlock}>
              <Text style={styles.stateText}>{loadError}</Text>
              <TouchableOpacity style={styles.primaryBtn} onPress={load} accessibilityRole="button">
                <Text style={styles.primaryBtnText}>Try again</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <>
              {Number.isFinite(pct) && pct > 0 ? <Text style={styles.intro}>{introText(pct)}</Text> : null}
              {available ? (
                <>
                  {renderChecklist()}
                  {renderAccept()}
                  {renderTiers()}
                </>
              ) : (
                <Text style={styles.notice}>{"New members can't join right now. Current members keep their perks and renewals."}</Text>
              )}
              {renderPerks(available)}
              {renderChat(available)}
              {renderMembers()}
              {renderBlocked()}
              {renderEndAll()}
            </>
          )}
        </View>
      </ScrollView>

      <Modal visible={!!spec} transparent animationType="fade" onRequestClose={() => setConfirm(null)}>
        <View style={styles.dgOverlay}>
          <Pressable style={styles.dgScrim} onPress={() => setConfirm(null)} />
          {spec ? (
            <View style={styles.dgCard}>
              <Text style={styles.dgTitle}>{spec.title}</Text>
              {spec.body.map((line) => <Text key={line} style={styles.dgBody}>{line}</Text>)}
              <View style={styles.dgBtnRow}>
                <TouchableOpacity style={styles.dgCancel} onPress={() => setConfirm(null)} accessibilityRole="button">
                  <Text style={styles.dgCancelTxt}>{spec.cancel}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={spec.danger ? styles.dgDanger : styles.dgConfirm}
                  onPress={spec.onOk}
                  disabled={!!busy}
                  accessibilityRole="button"
                >
                  {busy
                    ? <ActivityIndicator size="small" color={spec.danger ? '#fff' : '#000'} />
                    : <Text style={spec.danger ? styles.dgDangerTxt : styles.dgConfirmTxt}>{spec.ok}</Text>}
                </TouchableOpacity>
              </View>
            </View>
          ) : null}
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: C.bg },
  scroll: { padding: 16, paddingBottom: 48 },
  inner: { width: '100%', maxWidth: 760, alignSelf: 'center' },
  title: { color: C.text, fontSize: 26, fontWeight: '800', marginBottom: 12 },
  intro: { color: C.textMuted, fontSize: 14, lineHeight: 21, marginBottom: 16 },
  notice: { color: C.text, fontSize: 13.5, lineHeight: 20, marginTop: 8, marginBottom: 12 },

  stateBlock: { alignItems: 'center', paddingVertical: 48, paddingHorizontal: 16, gap: 12 },
  stateText: { color: C.textMuted, fontSize: 14, textAlign: 'center', maxWidth: 420, lineHeight: 20 },

  section: {
    backgroundColor: C.surface, borderWidth: 1, borderColor: C.border,
    borderRadius: THEME.radius.lg, padding: 16, marginBottom: 12,
  },
  sectionTitle: { color: C.text, fontSize: 16, fontWeight: '800', marginBottom: 10 },
  hint: { color: C.textMuted, fontSize: 12.5, lineHeight: 18, marginTop: 8 },

  checkRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8 },
  checkIcon: { marginRight: 10, marginTop: 1 },
  checkBody: { flex: 1, minWidth: 0 },
  checkText: { color: C.text, fontSize: 13.5, lineHeight: 20 },
  linkBtn: { alignSelf: 'flex-start', paddingVertical: 6 },
  linkBtnText: { color: C.brand, fontSize: 13.5, fontWeight: '700' },

  switchRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingVertical: 6 },
  switchLabel: { flex: 1, minWidth: 0, color: C.text, fontSize: 14 },

  rungEst: { color: C.textMuted, fontSize: 12.5, lineHeight: 18, marginTop: 4 },
  sectionLead: { color: C.textMuted, fontSize: 13, lineHeight: 19, marginBottom: 10 },

  // Tiers (R5)
  tierCard: {
    backgroundColor: C.chip, borderWidth: 1, borderColor: C.borderStrong,
    borderRadius: THEME.radius.md, padding: 12, marginBottom: 8,
  },
  tierCardOn: { borderColor: C.brand },
  tierCardName: { flex: 1, minWidth: 0, color: C.text, fontSize: 15, fontWeight: '800' },
  stepRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 6 },
  stepBtn: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1, borderColor: C.borderStrong,
    alignItems: 'center', justifyContent: 'center', backgroundColor: C.surface,
  },
  stepBtnOff: { opacity: 0.4 },
  stepPrice: { color: C.text, fontSize: 16, fontWeight: '800', minWidth: 120, textAlign: 'center' },
  bandText: { color: C.textMuted, fontSize: 12, lineHeight: 17, marginTop: 4 },
  saveBtn: {
    alignSelf: 'flex-start', backgroundColor: C.brand, borderRadius: THEME.radius.md,
    paddingHorizontal: 14, paddingVertical: 8, marginTop: 10,
  },
  saveBtnText: { color: '#000', fontWeight: '800', fontSize: 13 },
  perkBlock: { paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: C.border },
  startsAtLabel: { color: C.textMuted, fontSize: 12, marginTop: 6 },
  pickRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  pickChip: {
    borderWidth: 1, borderColor: C.borderStrong, borderRadius: 999,
    paddingHorizontal: 12, paddingVertical: 6, backgroundColor: C.chip,
  },
  pickChipOn: { backgroundColor: C.brand, borderColor: C.brand },
  pickChipText: { color: C.text, fontSize: 12.5, fontWeight: '700' },
  pickChipTextOn: { color: '#000' },
  summary: { marginTop: 12, paddingTop: 10, borderTopWidth: 1, borderTopColor: C.border },
  summaryTitle: { color: C.text, fontSize: 14, fontWeight: '800', marginBottom: 6 },
  summaryRow: { marginTop: 6 },
  summaryTier: { color: C.text, fontSize: 13.5, fontWeight: '700' },
  summaryLine: { color: C.textMuted, fontSize: 12.5, lineHeight: 18, marginLeft: 6 },
  tierCounts: { color: C.textMuted, fontSize: 12.5, marginTop: -4, marginBottom: 6 },

  memberRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingVertical: 10, borderTopWidth: 1, borderTopColor: C.border,
  },
  memberBody: { flex: 1, minWidth: 0 },
  memberName: { color: C.text, fontSize: 14, fontWeight: '700' },
  memberMeta: { color: C.textMuted, fontSize: 12.5, marginTop: 2 },
  chipRow: { flexDirection: 'row', gap: 6, marginTop: 4 },
  chip: {
    color: C.text, fontSize: 11, fontWeight: '800', backgroundColor: C.chip,
    borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2, overflow: 'hidden',
  },
  chipWarn: { color: '#ff9c9c' },
  chipTier: { color: C.brand },
  smallBtn: {
    backgroundColor: C.chip, borderWidth: 1, borderColor: C.borderStrong,
    borderRadius: THEME.radius.md, paddingHorizontal: 12, paddingVertical: 8, flexShrink: 0,
  },
  smallBtnText: { color: C.text, fontSize: 12.5, fontWeight: '700' },
  smallBtnDanger: { color: '#ff6b6b', fontSize: 12.5, fontWeight: '700' },
  dangerBtn: {
    borderWidth: 1, borderColor: '#e5484d', borderRadius: THEME.radius.md, paddingVertical: 12, alignItems: 'center',
  },
  dangerBtnText: { color: '#ff6b6b', fontWeight: '800', fontSize: 14 },
  primaryBtn: {
    backgroundColor: C.brand, borderRadius: THEME.radius.md, paddingHorizontal: 16, paddingVertical: 10, alignItems: 'center',
  },
  primaryBtnText: { color: '#000', fontWeight: '800', fontSize: 13.5 },

  dgOverlay: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 20 },
  dgScrim: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.75)' },
  dgCard: {
    width: '100%', maxWidth: 400, backgroundColor: C.surface, borderWidth: 1, borderColor: C.borderStrong,
    borderRadius: THEME.radius.lg, padding: 20,
  },
  dgTitle: { color: C.text, fontSize: 17, fontWeight: '800', marginBottom: 10 },
  dgBody: { color: C.textMuted, fontSize: 13.5, lineHeight: 20, marginBottom: 10 },
  dgBtnRow: { flexDirection: 'row', gap: 10, marginTop: 4 },
  dgCancel: {
    flex: 1, backgroundColor: C.chip, borderWidth: 1, borderColor: C.borderStrong,
    borderRadius: THEME.radius.md, paddingVertical: 12, alignItems: 'center',
  },
  dgCancelTxt: { color: C.text, fontWeight: '700', fontSize: 13.5 },
  dgConfirm: { flex: 1, backgroundColor: C.brand, borderRadius: THEME.radius.md, paddingVertical: 12, alignItems: 'center' },
  dgConfirmTxt: { color: '#000', fontWeight: '800', fontSize: 13.5 },
  dgDanger: { flex: 1, backgroundColor: '#e5484d', borderRadius: THEME.radius.md, paddingVertical: 12, alignItems: 'center' },
  dgDangerTxt: { color: '#fff', fontWeight: '800', fontSize: 13.5 },
});
